import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { DialogService } from '@services/dialog/dialog.service';
import { Models } from '@ibexfusion/models';
import { AppPickerDialogComponent } from './app-picker-dialog/app-picker-dialog.component';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { PackageInstalledWidgetsService } from '@services/packages/package-installed-widgets.service';
import { PackageService } from '@services/packages/package.service';
import { CompositionAppService } from '@services/compositions/composition-app.service';
import { CompositionService } from '@services/compositions/composition.service';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { CodeSnippetService } from '@services/code-snippets/code-snippet.service';
import { TranslateService } from '@services/translate/translate.service';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { OpenReadmeService } from '@services/readme/open-readme.service';
import { AppHighscoreResetDialogComponent } from './app-highscore-reset-dialog/app-highscore-reset-dialog.component';
import { MessengerService } from '@services/messenger/messenger.service';
import { ApplicationGenerationComponent } from '../../../components/application-generation/application-generation.component';
import { ImportService } from '@services/imports/import.service';
import { AppTranslationMap } from '../../../maps/app-translation.map';

export interface PackageInstalledData {
  packageInstalledApp: Models.PackageInstalledApp,
  packageInstalledWidget: Models.PackageInstalledWidget,
  compositionApp: Models.CompositionApp,
  dynamicContentEntranceApp: Models.DynamicContentEntrance,
  codeSnippetApp: Models.CodeSnippet,
  title?: string
}

@Component({
    selector: 'app-package-installed-apps-manager',
    templateUrl: './package-installed-apps-manager.component.html',
    styleUrls: ['./package-installed-apps-manager.component.scss'],
    providers: [PackageInstalledAppsService, PackageInstalledWidgetsService, PackageService, ImportService,
        CompositionAppService, CompositionService, DynamicContentEntranceService, CodeSnippetService],
    standalone: false
})
export class PackageInstalledAppsManagerComponent implements OnInit, OnDestroy {

  public packageId;
  public package: Models.Package;

  orders = ['installedApp', 'installedWidget', 'installedMobileApp', 'installedMobileWidget'];
  activeOrder = 'installedApp';

  @Input() onSave: Observable<any>;
  public superadmin = false;
  public messages;
  public importAll = false;
  public compositions: Models.Composition[] = [];
  public isMasterPackage = false;
  public installedApps: PackageInstalledData[];
  public installedWidgets: PackageInstalledData[];
  public installedMobileApps: PackageInstalledData[];
  public installedMobileWidgets: PackageInstalledData[];
  public installedAppsMap: Map<string, Models.PackageInstalledApp>;
  public installedWidgetsMap: Map<string, Models.PackageInstalledWidget>;
  public compositionAppsMap: Map<string, Models.CompositionApp>;
  public dynamicContentEntrancesMap: Map<string, Models.DynamicContentEntrance>;
  public codeSnippetMap: Map<string, Models.CodeSnippet>;
  public availableCompositionApps: Models.CompositionApp[] = [];
  public availableDynamicContent: Models.DynamicContentEntrance[] = [];
  public availableCodeSnippets: Models.CodeSnippet[] = [];
  public availableLanguages: any[];
  public selectedIndex = 0;
  public selectedAppIndex = -1;
  public activeInstalledApp = null;
  public isLoading = true;
  public isScreenPackage = true;
  public initialized = false;
  private subscription: any;
  private now = { m: new Date().getMonth(), d: new Date().getDate() };

  constructor(
    private activatedRoute: ActivatedRoute, private packageService: PackageService, private compositionService: CompositionService, private appService: PackageInstalledAppsService,
    private widgetService: PackageInstalledWidgetsService, private entranceService: DynamicContentEntranceService, private ms: MessengerService,
    private importService: ImportService, private dialog: MatDialog, private compositionAppService: CompositionAppService, private snippetService: CodeSnippetService,
    private dialogService: DialogService, private ts: TranslateService, private readme: OpenReadmeService, private snackbar: SnackbarService
  ) {
  }

  /* Lifecycle hooks */
  async ngOnInit() {
    this.messages = this.ts.get('system');
    this.subscription = this.onSave.subscribe(type => {
      if (!this.initialized) {
        return;
      }
      if (type === 'app-manager') {
        this.savePackage();
        if (this.selectedAppIndex > -1) {
          console.log(1, 'todo: we should save these changes too')
        }
      }
      if (type === 'app-manager-reload') {
        this.reloadPackage();
      }
    });

    const privileges = this.ms.$superadminPrivileges.getValue();

    if (!!privileges) {
      for (const privilege of privileges) {
        if (privilege.privilegeName === 'superadmin' && privilege.value === 'packages') {
          this.superadmin = true;
        }
      }
    }

    this.packageId = this.activatedRoute.snapshot.params['id'];
    const myPackage = await this.packageService.get(this.packageId);
    this.package = myPackage;
    if (this.package.assignedChildPackages && this.package.assignedChildPackages.length) {
      this.isMasterPackage = true;
    }
    this.isScreenPackage = !!myPackage.isScreenPackage;
    if (!this.isScreenPackage) {
      this.orders = ['installedApp'];
    }
    const masterPackages = await this.packageService.find(0, 10, { assignedChildPackages: this.packageId });
    if (masterPackages && masterPackages.data && masterPackages.data.length) {
      for (const masterPackage of masterPackages.data) {
        for (const key of ['installedAppIds', 'installedMobileAppIds', 'installedWidgetIds', 'installedMobileWidgetIds']) {
          for (const id of masterPackage[key]) {
            if (this.package[key].indexOf(id) === -1) {
              this.package[key].push(id);
            }
          }
        }
      }
    }
    const compositions = await this.compositionService.find(0, 100, { _id: { $in: this.package.compositionIds } });
    this.compositions = compositions.data;
    if (!this.package.tweebie.isMaster) {
      await this.fetchData();
      this.initialized = true;
      this.isLoading = false;
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  /* public functions */
  public getActiveTypeArray(): any[] {
    return this.getTypeArray(this.activeOrder);
  }

  public getTypeArray(type) {
    return this[type + 's'] || this.installedApps;
  }

  public setActiveTypeArray(data) {
    if (this[this.activeOrder + 's']) {
      this[this.activeOrder + 's'] = data;
    } else {
      this.installedApps = data;
    }
  }

  public changeActiveType(event: any) {
    this.activeOrder = this.orders[event];
  }

  public async openAppPicker() {
    let dialogRef: MatDialogRef<AppPickerDialogComponent>;
    /*const config = new MatDialogConfig();*/

    dialogRef = this.dialog.open(AppPickerDialogComponent, {
      data: {
        availableCompositionApps: this.availableCompositionApps,
        availableDynamicContent: this.availableDynamicContent,
        availableCodeSnippets: this.availableCodeSnippets
      }
    });
    /*
        dialogRef.componentInstance.availableCompositionApps = this.availableCompositionApps;
        dialogRef.componentInstance.availableDynamicContent = this.availableDynamicContent;
        dialogRef.componentInstance.availableCodeSnippets = this.availableCodeSnippets;*/

    const pickedApps = await dialogRef.afterClosed().toPromise();
    if (!pickedApps || pickedApps.length === 0) {
      return false;
    }
    this.installNewApps(pickedApps);
  }

  async openAppCreator() {
    const dialogRef = this.dialog.open(ApplicationGenerationComponent, { width: '75vw' });
    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }
    await this.createApp(result, this.package.compositionIds[this.package.compositionIds.length - 1]);
  }

  async createApp(app, compositionId) {
    let entranceId, compositionAppId, codeSnippetId;
    const comp = await this.compositionService.get(compositionId);
    if (app.type === 'dynamicContent') {
      const entrance: any = { title: app.title, type: app.subType, OwnerOrganisationID: this.package.OwnerOrganisationID, OwnerCustomerID: this.package.customerId };
      if (app.subType === 'memory') {
        entrance.type = 'gallery';
        entrance.config = { overwriteContentType: 'memory' };
      } else if (app.subType === 'new') {
        entrance.type = 'information';
        entrance.config = { overwriteContentType: 'news', renderAsRelated: true, startLevel: 1 };
      }
      const res = await this.entranceService.create(entrance);
      comp.dynamicContentIds ??= [];
      comp.dynamicContentIds.push(res._id);
      entranceId = res._id;
    } else if (app.type === 'import') {
      let importIds = [];
      if (app.languageSpecific) {
        for (const key of Object.keys(app.importsByLanguage)) {
          importIds = importIds.concat(_.map(app.importsByLanguage[key], 'id'));
        }
      } else {
        importIds = importIds.concat(_.map(app.imports, 'id'));
      }
      const imports = (await this.importService.find(0, importIds.length, { _id: { $in: importIds } })).data;
      comp.importIds ??= [];
      comp.importIds = comp.importIds.concat(importIds);
      const translations = [];
      if (app.languageSpecific) {
        const menuItemsByLanguage = {};
        for (const language of Object.keys(app.importsByLanguage)) {
          for (const importElement of app.importsByLanguage[language]) {
            for (const contentImport of imports) {
              if (contentImport._id.toString() === importElement.id.toString()) {
                if (!menuItemsByLanguage[language]) {
                  menuItemsByLanguage[language] = [];
                }
                menuItemsByLanguage[language].push(contentImport.assignableMenuItems[0].menuItemId);
              }
            }
          }
          translations.push({ language, appTitle: app.title, menuItemIds: menuItemsByLanguage[language] });
        }
      } else {
        const menuItemIds = [];
        for (const importElement of app.imports) {
          for (const contentImport of imports) {
            if (contentImport._id.toString() === importElement.id.toString()) {
              if (contentImport.assignableMenuItems && contentImport.assignableMenuItems[0] && contentImport.assignableMenuItems[0].menuItemId) {
                menuItemIds.push(contentImport.assignableMenuItems[0].menuItemId);
              }
            }
          }
        }
        for (const language of this.package.availableLanguagesCached) {
          translations.push({ language, appTitle: app.title, menuItemIds });
        }
      }
      const compositionApp = await this.compositionAppService.create({ compositionId: compositionId, name: app.title, translations });
      compositionAppId = compositionApp._id;
    } else {
      codeSnippetId = app.subType;
      comp.snippetIds ??= [];
      comp.snippetIds.push(app.subType);
    }

    await this.compositionService.patch(compositionId, { impoortIds: comp.importIds, snippetIds: comp.snippetIds, dynamicContentIds: comp.dynamicContentIds });
    const languages = this.package?.availableLanguagesCached?.length ? this.package.availableLanguagesCached : comp?.screen?.availableLanguages;
    const appRes = await this.createAppAndWidget({ app, entranceId, compositionAppId, codeSnippetId, languages, compositionId });
    this.package.installedAppIds.push(appRes.appId);
    this.package.installedMobileAppIds.push(appRes.appId);
    this.package.installedWidgetIds.push(appRes.widgetId);
    this.package.installedMobileWidgetIds.push(appRes.widgetId);
    await this.packageService.patch(this.package._id, {
      installedAppIds: this.package.installedAppIds, installedWidgetIds: this.package.installedWidgetIds,
      installedMobileAppIds: this.package.installedMobileAppIds, installedMobileWidgetIds: this.package.installedMobileWidgetIds
    });
    await this.fetchData();
  }

  private async createAppAndWidget(data) {
    const app = data.app;
    const appTranslations = [];
    const widgetTranslations = [];
    for (const language of data.languages) {
      appTranslations.push({ appTitle: app.title, language });
      widgetTranslations.push({ language });
    }
    const installedWidget = {
      packageId: this.package._id, disabled: false, screenDisabled: false, mobileDisabled: false,
      default: true, translations: widgetTranslations
    };
    const widgetResult = await this.widgetService.create(installedWidget);
    const installedApp = {
      packageId: this.package._id, translations: appTranslations, packageInstalledWidgetId: widgetResult._id,
      compositionId: data.compositionId, iconOverride: '', disabled: false, screenDisabled: false,
      mobileDisabled: false, isSecured: false, skipWhenOnlyChild: app.type === 'import', dynamicContentEntranceId: data.entranceId,
      compositionAppId: data.compositionAppId, codeSnippetId: data.codeSnippetId,
    };
    installedApp.dynamicContentEntranceId = data.entranceId;
    installedApp.dynamicContentEntranceId = data.entranceId;
    const appResult = await this.appService.create(installedApp);
    return { widgetId: widgetResult._id, appId: appResult._id }
  }

  public importAllApps() {
    const allApps = _.merge(this.availableDynamicContent, this.availableCompositionApps);
    this.installNewApps(allApps);
    this.importAll = false;
  }

  public async toggleMasterPackageApp(appId) {
    if (!this.package.disabledInheritedAppIds) {
      this.package.disabledInheritedAppIds = [];
    }
    if (this.package.disabledInheritedAppIds.indexOf(appId) > -1) {
      this.package.disabledInheritedAppIds.splice(this.package.disabledInheritedAppIds.indexOf(appId), 1);
    } else {
      this.package.disabledInheritedAppIds.push(appId);
    }

    await this.packageService.patch(this.package._id, { disabledInheritedAppIds: this.package.disabledInheritedAppIds });
    this.snackbar.success(this.messages.general.savedChanges, 2000);
  }

  public async onWidgetSave(widget: Models.PackageInstalledWidget): Promise<any> {
    await this.widgetService.update(widget._id, widget);
    this.snackbar.success(this.messages.general.savedChanges, 2000);
  }

  public async onInstalledAppSave(): Promise<any> {
    const installedApp = this.getActiveTypeArray()[this.selectedAppIndex].packageInstalledApp;
    await this.appService.update(installedApp._id, installedApp);
    this.installedApps = this.getTitles(this.installedApps);
    this.installedWidgets = this.getTitles(this.installedWidgets);
    this.installedMobileApps = this.getTitles(this.installedMobileApps);
    this.installedMobileWidgets = this.getTitles(this.installedMobileWidgets);
    this.snackbar.success(this.messages.general.savedChanges, 2000);
  }

  public async onInstalledAppRemoved(index: any): Promise<any> {
    const res = await this.dialogService.confirm(this.messages.packages['removeConfirm']);
    if (!res) {
      return;
    }
    index = _.clone(this.selectedAppIndex);
    this.selectedAppIndex = -1;
    this.activeInstalledApp = null;
    const installedApp = this.getActiveTypeArray()[index];
    await this.appService.remove(installedApp.packageInstalledApp._id);
    if (installedApp.packageInstalledWidget && installedApp.packageInstalledWidget._id) {
      await this.widgetService.remove(installedApp.packageInstalledWidget._id);
    }
    this.removeAppFromLists(installedApp.packageInstalledApp._id);
    await this.saveOrder();
    this.snackbar.success(this.messages.general.savedChanges, 2000);
  }

  public async onResetHighscore() {
    const uuids = this.package.screenUuids;
    if (this.package.assignedChildPackages && this.package.assignedChildPackages.length) {
      const result = await this.packageService.find(0, 1000, { _id: { $in: this.package.assignedChildPackages } });
      if (result && result.data && result.data.length) {
        result.data.map(childPackage => {
          uuids.push(...childPackage.screenUuids);
        });
      }
    }
    uuids.push(this.package._id.toString());
    let dialogRef: MatDialogRef<AppHighscoreResetDialogComponent>;
    const config = new MatDialogConfig();
    dialogRef = this.dialog.open(AppHighscoreResetDialogComponent, config);
    dialogRef.componentInstance.uuids = uuids;
  }

  /* TODO: check this function for widgets and others (superadmin) */
  public checkActive(app) {
    const from = app.from ? { m: app.from.getMonth(), d: app.from.getDate() } : null;
    const to = app.to ? { m: app.to.getMonth(), d: app.to.getDate() } : null;
    let active = true;
    if (from && to) {
      active = false;
      const info = {
        fromBeforeTo: from.m < to.m || (from.m === to.m && from.d <= to.d),
        fromBeforeNow: from.m < this.now.m || (from.m === this.now.m && from.d <= this.now.d),
        toAfterNow: to.m > this.now.m || (to.m === this.now.m && to.d >= this.now.d)
      };

      if (info.fromBeforeTo) {  // to is in same year
        if (info.fromBeforeNow && info.toAfterNow) {
          active = true;
        }
      } else if (info.fromBeforeNow && (to.m < this.now.m || (to.m === this.now.m && to.d >= this.now.d))) { // to is next year and from is this year
        active = true;
      } else if (!info.fromBeforeNow && info.toAfterNow) { // to is last year, from is this year
        active = true;
      }
    }
    if (this.package.disabledInheritedAppIds && this.package.disabledInheritedAppIds.indexOf(app._id) > -1) {
      return 'disabledInherited';
    }
    return active && !app.disabled ? 'active' : 'paused';
  }

  public async dropItem(event: any, i?: number) {
    const data = this.getActiveTypeArray();
    if (event.previousIndex !== event.currentIndex) {
      this.isLoading = true;
      const item = data[event.previousIndex];
      data.splice(event.previousIndex, 1);
      data.splice(event.currentIndex, 0, item);
      this.setActiveTypeArray(data);
      if (this.selectedAppIndex === event.previousIndex) {
        this.selectedAppIndex = -2;
      }
      await this.saveOrder();
      this.selectedAppIndex = this.selectedAppIndex === -2 ? i : -1;
      this.isLoading = false;
    }
  }

  public select(index) {
    this.activeInstalledApp = null;
    setTimeout(() => {
      this.activeInstalledApp = index === null ? null : (this.selectedAppIndex === index ? null : this.getActiveTypeArray()[index]);
      this.selectedAppIndex = index === null ? -1 : (this.selectedAppIndex === index ? -1 : index);
    }, 1);
  }

  public readMe(name, tag) {
    this.readme.openReadMe({ tag: tag, name: name });
  }

  public resetFixedWidget() {
    this.package.screen.fixedWidgetId = undefined;
  }

  public tabChange(event) {
    this.selectedIndex = event.index
  }

  public async resetAppOrders() {
    const widgetIds = [];
    for (let i = 0; i < this.package.installedAppIds.length; i++) {
      const packageInstalledAppId = this.package.installedAppIds[i];
      if (this.installedAppsMap.get(packageInstalledAppId)) {
        widgetIds.push(this.installedAppsMap.get(packageInstalledAppId).packageInstalledWidgetId);
      } else {
        this.package.installedAppIds.splice(i, 1);
      }
    }

    const res = this.packageService.find(0, 1, { assignedChildPackages: this.package._id });
    if (res && res[0] && res[0].installedAppIds && res[0].installedAppIds.length) {
      for (const id of res[0].installedAppIds) {
        if (!this.package.installedAppIds.includes(id)) {
          this.package.installedAppIds.push(id);
        }
      }
    }
    if (res && res[0] && res[0].installedWidgetIds && res[0].installedWidgetIds.length) {
      for (const id of res[0].installedWidgetIds) {
        if (!widgetIds.includes(id)) {
          widgetIds.push(id);
        }
      }
    }
    await this.packageService.patch(this.package._id, {
      installedAppIds: this.package.installedAppIds, installedMobileAppIds: this.package.installedAppIds,
      installedWidgetIds: widgetIds, installedMobileWidgetIds: widgetIds
    });
    this.package = null;
    return this.ngOnInit();
  }

  /* private functions */
  private async reloadPackage() {
    this.isLoading = true;
    this.package = await this.packageService.get(this.packageId);
    this.isLoading = false;
  }

  private async installNewApps(pickedApps, disabled = false): Promise<any> {
    for (const app of pickedApps) {
      const newInstalledApp = new Models.PackageInstalledApp();
      const newInstalledWidget = new Models.PackageInstalledWidget();
      newInstalledApp.populateFromJSON({ iconOverride: undefined, packageId: this.package._id, translations: [], disabled });
      newInstalledWidget.populateFromJSON({ packageId: this.package._id, translations: [], disabled });
      if (app instanceof Models.CompositionApp) {
        Object.assign(newInstalledApp, {
          compositionAppId: app._id, compositionId: app.compositionId, translations: this.createTranslations(app, 'compositionApp'), skipWhenOnlyChild: true
        });
      } else if (app instanceof Models.DynamicContentEntrance) {
        const comp = this.compositions.find(c => c.dynamicContentIds.includes(app._id));
        Object.assign(newInstalledApp, {
          dynamicContentEntranceId: app._id, compositionId: comp._id, translations: this.createTranslations(app, 'dynamicContent'), iconOverride: this.getDynamicContentIcon(app)
        });
      } else if (app instanceof Models.CodeSnippet) {
        const comp = this.compositions.find(c => c.snippetIds.includes(app._id));
        Object.assign(newInstalledApp, { codeSnippetId: app._id, compositionId: comp._id, translations: this.createTranslations(app, 'codeSnippet'), });
      }

      if (this.package.isScreenPackage) {
        newInstalledWidget.translations = this.createWidgetTranslations(app);
        const widgetRes = await this.widgetService.create(newInstalledWidget);
        if (widgetRes._id) {
          newInstalledApp.packageInstalledWidgetId = widgetRes._id;
        }
      }
      const installedApp = await this.appService.create(newInstalledApp);
      this.package.installedAppIds = this.package.installedAppIds || [];
      this.package.installedMobileAppIds = this.package.installedMobileAppIds || [];
      this.package.installedAppIds.push(installedApp._id);
      this.package.installedMobileAppIds.push(installedApp._id);

      if (this.package.isScreenPackage) {
        this.package.installedWidgetIds = this.package.installedWidgetIds || [];
        this.package.installedMobileWidgetIds = this.package.installedMobileWidgetIds || [];
        this.package.installedWidgetIds.push(installedApp.packageInstalledWidgetId);
        this.package.installedMobileWidgetIds.push(installedApp.packageInstalledWidgetId);
      }

      await this.packageService.patch(this.package._id, {
        installedAppIds: this.package.installedAppIds, installedMobileAppIds: this.package.installedMobileAppIds,
        installedWidgetIds: this.package.installedWidgetIds, installedMobileWidgetIds: this.package.installedMobileWidgetIds,
      });

      let childPackages = [];
      let childIds = this.package.assignedChildPackages;
      while (childIds?.length) {
        const children = (await this.packageService.find(0, 1000, { _id: { $in: childIds } })).data;
        childPackages = childPackages.concat(children);
        childIds = children.map(child => child.assignedChildPackages).reduce((acc, val) => acc.concat(val), []);
      }
      for (const pak of childPackages) {
        if (!pak.installedAppIds.includes(installedApp._id)) { pak.installedAppIds.push(installedApp._id); }
        if (!pak.installedMobileAppIds.includes(installedApp._id)) { pak.installedMobileAppIds.push(installedApp._id); }
        if (!pak.installedWidgetIds.includes(installedApp._id)) { pak.installedWidgetIds.push(installedApp._id); }
        if (!pak.installedMobileWidgetIds.includes(installedApp._id)) { pak.installedMobileWidgetIds.push(installedApp._id); }
        await this.packageService.patch(pak._id, {
          installedAppIds: pak.installedAppIds, installedMobileAppIds: pak.installedMobileAppIds,
          installedWidgetIds: pak.installedWidgetIds, installedMobileWidgetIds: pak.installedMobileWidgetIds
        });
      }
    }

    await this.fetchData();
  }

  private createTranslations(pickedApp, type) {
    const translations = [];
    this.availableLanguages.map(language => {
      const newTranslation = new Models.PackageInstalledAppTranslation();
      let appTitle = type === 'compositionApp' ? pickedApp.name : pickedApp.title;
      if (type === 'dynamicContent') {
        appTitle = AppTranslationMap[appTitle?.toLowerCase()]?.[language] ?? appTitle;
      }
      newTranslation.populateFromJSON({ language, hidden: false, appTitle, });
      translations.push(newTranslation)
    });
    return translations;
  }

  private createWidgetTranslations(pickedApp) {
    const translations = [];
    this.availableLanguages.map(language => {
      const newTranslation = new Models.PackageInstalledWidgetTranslation();
      newTranslation.populateFromJSON({ language, hidden: false, title: null, });
      translations.push(newTranslation)
    });
    return translations;
  }

  private removeAppFromLists(packageInstalledAppId) {
    const lists = ['installedApps', 'installedWidgets', 'installedMobileApps', 'installedMobileWidgets'];
    for (const name of lists) {
      for (let i = 0; i < this[name].length; i++) {
        if (this[name][i] && this[name][i].packageInstalledApp && this[name][i].packageInstalledApp._id === packageInstalledAppId) {
          this[name].splice(i, 1);
        }
      }
    }
  }

  private async saveOrder(): Promise<boolean> {
    this.package = await this.packageService.patch(this.package._id, {
      installedAppIds: this.getIds(this.installedApps), installedWidgetIds: this.getIds(this.installedWidgets, 'packageInstalledWidget'),
      installedMobileAppIds: this.getIds(this.installedMobileApps), installedMobileWidgetIds: this.getIds(this.installedMobileWidgets, 'packageInstalledWidget'),
    });
    this.snackbar.success(this.messages.general.savedChanges, 2000);
    await this.fetchData();
    return true;
  }

  private async savePackage(): Promise<any> {
    await this.packageService.update(this.package._id, this.package);
    this.snackbar.success(this.messages.general.savedChanges, 2000);
  }

  private async fetchData(): Promise<boolean> {
    // get available Apps
    this.installedApps = [];
    this.installedWidgets = [];
    this.installedMobileApps = [];
    this.installedMobileWidgets = [];
    this.availableCompositionApps = [];
    this.availableDynamicContent = [];
    this.availableCodeSnippets = [];
    let dynamicContentEntranceIds = [];
    let codeSnippetIds = [];

    this.compositions.map(composition => {
      this.availableLanguages = composition.screen.availableLanguages;
      if (!!composition.dynamicContentIds) {
        dynamicContentEntranceIds = _.union(dynamicContentEntranceIds, composition.dynamicContentIds)
      }
      if (!!composition.snippetIds) {
        codeSnippetIds = _.union(codeSnippetIds, composition.snippetIds);
      }
    });

    await this.fetchInstalledApps();
    await this.fetchInstalledWidgets();
    await this.fetchCompositionApps();
    await this.fetchDynamicContentEntrances(dynamicContentEntranceIds);
    await this.fetchCodeSnippets(codeSnippetIds);
    if (this.package.compositionIds.length && this.compositionAppsMap.size && this.installedAppsMap.size < this.compositionAppsMap.size) {
      this.importAll = true;
    }

    this.package.installedAppIds.map(id => {
      if (!this.getPackageInstalledData(id)) {
        return;
      }
      this.installedApps.push(this.getPackageInstalledData(id));
    });
    this.installedApps = this.getTitles(this.installedApps);

    const groups = { installedWidgetIds: 'packageInstalledWidget', installedMobileAppIds: 'packageInstalledApp', installedMobileWidgetIds: 'packageInstalledWidget' };
    for (const key of Object.keys(groups)) {
      if (!this.package[key]) {
        this.package[key] = [];
      }
      if (!this.package[key].length) {
        for (const installedApp of this.installedApps) {
          if (installedApp[groups[key]] && installedApp[groups[key]]._id) {
            this.package[key].push(installedApp[groups[key]]._id);
          }
        }
      }
      const idLessKey = key.replace('Id', '');
      this.package[key].map(id => {
        if (!!this.getPackageInstalledData(id)) {
          this[idLessKey].push(this.getPackageInstalledData(id));
        }
      });
      if (!this[idLessKey] || !this[idLessKey].length) {
        this[idLessKey] = _.cloneDeep(this.installedApps);
      }
      this[idLessKey] = this.getTitles(this[idLessKey], groups[key]);
    }
    return Promise.resolve(true);
  }

  private getIds(array, field = 'packageInstalledApp'): string[] {
    return array.map(element => {
      return ((!!element[field] && !!element[field]._id) ? element[field]._id : element.packageInstalledApp._id);
    })
  }

  private getPackageInstalledData(id): PackageInstalledData {
    let installedApp = this.installedAppsMap.get(id);
    if (!installedApp) {
      for (const element of this.installedApps) {
        if (element && element.packageInstalledWidget && element.packageInstalledWidget._id === id) {
          installedApp = element.packageInstalledApp;
        }
      }
    }
    if (!installedApp) {
      return;
    }

    const installedWidget = this.installedWidgetsMap.get(installedApp.packageInstalledWidgetId);
    return {
      packageInstalledApp: installedApp,
      packageInstalledWidget: installedWidget,
      compositionApp: this.compositionAppsMap.get(installedApp.compositionAppId),
      dynamicContentEntranceApp: this.dynamicContentEntrancesMap.get(installedApp.dynamicContentEntranceId),
      codeSnippetApp: this.codeSnippetMap.get(installedApp.codeSnippetId)
    };
  }

  private getTitles(data, titleSource = 'packageInstalledApp') {
    for (let i = 0; i < data.length; i++) {
      let title: string;
      if (data && data[i] && data[i][titleSource] && data[i][titleSource].translations) {
        for (const trans of data[i][titleSource].translations) {
          if (trans.language === 'de' || (trans.language !== 'de' && !title)) {
            title = trans.appTitle || trans.title;
            if (this.dynamicContentEntrancesMap.get(data[i][titleSource].dynamicContentEntranceId)) {
              title += ' - ' + this.dynamicContentEntrancesMap.get(data[i][titleSource].dynamicContentEntranceId).type;
            }
          }
        }
      }
      if (!title) {
        for (const trans of data[i].packageInstalledApp.translations) {
          if (trans.language === 'de') {
            title = trans.appTitle || trans.title;
            if (this.dynamicContentEntrancesMap.get(data[i].packageInstalledApp.dynamicContentEntranceId)) {
              title += ' - ' + this.dynamicContentEntrancesMap.get(data[i].packageInstalledApp.dynamicContentEntranceId).type;
            }
          }
        }
      }

      data[i].title = title;
    }
    return data;
  }

  private async fetchInstalledApps(): Promise<boolean> {
    this.installedAppsMap = new Map();

    if (!this.package || !this.package.installedAppIds || this.package.installedAppIds.length === 0) {
      return Promise.resolve(true);
    }
    const ids = _.cloneDeep(this.package.installedAppIds);
    if (!this.superadmin && this.package && this.package.disabledInheritedAppIds && this.package.disabledInheritedAppIds.length) {
      for (let i = 0; i < this.package.disabledInheritedAppIds.length; i++) {
        if (ids.includes(this.package.disabledInheritedAppIds[i])) {
          ids.splice(ids.indexOf(this.package.disabledInheritedAppIds[i]), 1);
        }
      }
    }
    const query = { _id: { $in: ids } };
    const response = await this.appService.find(0, 100, query);
    for (let i = 0; i < response.data.length; i++) {
      if (response.data[i].packageId !== this.packageId && response.data[i].disabled) {
        continue;
      }
      this.installedAppsMap.set(response.data[i]._id, response.data[i]); // set data of Installed Apps
    }
    return true;
  }

  private async fetchInstalledWidgets(): Promise<boolean> {
    this.installedWidgetsMap = new Map();
    if (!this.isScreenPackage) {
      return Promise.resolve(true);
    }
    const widgetIds = [];

    this.installedAppsMap.forEach(app => {
      if (app.packageInstalledWidgetId) {
        widgetIds.push(app.packageInstalledWidgetId);
      }
    });

    const query: any = [{ packageId: this.package._id }];
    if (widgetIds.length > 0) {
      query.push({ _id: { $in: widgetIds } });
    }
    const response = await this.widgetService.find(0, 100, { $or: query });
    for (let i = 0; i < response.data.length; i++) {
      this.installedWidgetsMap.set(response.data[i]._id, response.data[i]); // set data of Installed Apps
    }
    return true;
  }

  private async fetchCompositionApps(): Promise<boolean> {
    this.compositionAppsMap = new Map();
    const response = await this.compositionAppService.find(0, 100, { compositionId: { $in: this.package.compositionIds } });
    for (let i = 0; i < response.data.length; i++) {
      this.compositionAppsMap.set(response.data[i]._id, response.data[i]); // set composition App Data
      let isNotInstalled = true;
      this.installedAppsMap.forEach(item => {
        if (item.compositionAppId === response.data[i]._id) {
          isNotInstalled = false;
        }
      });
      if (isNotInstalled) {
        this.availableCompositionApps.push(response.data[i]);
      }
    }
    return true;
  }

  private async fetchDynamicContentEntrances(dynamicContentEntranceIds): Promise<boolean> {
    this.dynamicContentEntrancesMap = new Map();
    if (!dynamicContentEntranceIds || !dynamicContentEntranceIds.length) {
      return Promise.resolve(true);
    }
    let response: any;
    try {
      response = await this.entranceService.find(0, 100, { _id: { $in: dynamicContentEntranceIds } });
    } catch (err) {
      return true;
    }
    if (!response?.data) {
      return true;
    }
    for (let i = 0; i < response.data.length; i++) {
      this.dynamicContentEntrancesMap.set(response.data[i]._id, response.data[i]);
      let isNotInstalled = true;
      this.installedAppsMap.forEach(item => {
        if (item.dynamicContentEntranceId === response.data[i]._id) {
          isNotInstalled = false;
        }
      });
      if (isNotInstalled) {
        this.availableDynamicContent.push(response.data[i]);
      }
    }
  }

  private async fetchCodeSnippets(codeSnippetIds): Promise<boolean> {
    this.codeSnippetMap = new Map();
    if (!codeSnippetIds || !codeSnippetIds.length) {
      return Promise.resolve(true);
    }
    const response = await this.snippetService.find(0, 100, { _id: { $in: codeSnippetIds } });
    for (let i = 0; i < response.data.length; i++) {
      this.codeSnippetMap.set(response.data[i]._id, response.data[i]);
      let isNotInstalled = true;
      this.installedAppsMap.forEach(item => {
        if (item.codeSnippetId === response.data[i]._id) {
          isNotInstalled = false;
        }
      });
      if (isNotInstalled) {
        this.availableCodeSnippets.push(response.data[i]);
      }
    }
    return true;

  }
  private getDynamicContentIcon(app): string {
    return (AppTranslationMap.get(app.type) || AppTranslationMap.get(app.title?.toLowerCase()) || null)?.icon;
  }
}
